/**
 * Tiptap ResizableImage Extension
 * - 드래그로 이미지 크기 조절 가능
 * - width를 HTML attr로 저장하여 Yjs 협업에서도 동기화
 */
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useRef, useState, useCallback } from 'react';

const S3_DIRECT = 'https://prinmann-shop-uploads.s3.ap-northeast-2.amazonaws.com/';
function resolveImgSrc(src) {
  if (!src || !src.startsWith(S3_DIRECT)) return src;
  return `/api/s3/proxy?key=${encodeURIComponent(src.slice(S3_DIRECT.length))}`;
}

function ResizableImageView({ node, updateAttributes, selected }) {
  const containerRef = useRef(null);
  const [resizing, setResizing] = useState(false);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = containerRef.current?.offsetWidth || 300;

    const onMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(80, startWidth + diff);
      if (containerRef.current) containerRef.current.style.width = `${newWidth}px`;
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setResizing(false);
      const finalWidth = containerRef.current?.offsetWidth;
      if (finalWidth) updateAttributes({ width: finalWidth });
    };

    setResizing(true);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [updateAttributes]);

  const width = node.attrs.width;

  return (
    <NodeViewWrapper className="resizable-image-wrapper" data-drag-handle>
      <div
        ref={containerRef}
        className={`resizable-image-container ${selected ? 'selected' : ''} ${resizing ? 'resizing' : ''}`}
        style={{ width: width ? `${width}px` : '50%', display: 'inline-block', position: 'relative' }}
      >
        <img
          src={resolveImgSrc(node.attrs.src)}
          alt={node.attrs.alt || ''}
          title={node.attrs.title || ''}
          style={{ width: '100%', display: 'block' }}
          draggable={false}
        />
        {/* 우측 하단 리사이즈 핸들 */}
        <div
          className="resize-handle resize-handle-right"
          onMouseDown={onMouseDown}
        />
      </div>
    </NodeViewWrapper>
  );
}

const ResizableImage = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      title: { default: null },
      width: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'img[src]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = { ...HTMLAttributes };
    if (attrs.width) {
      attrs.style = `width: ${attrs.width}px`;
      delete attrs.width;
    }
    return ['img', mergeAttributes(attrs)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },

  addCommands() {
    return {
      setImage: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

export default ResizableImage;
